import _ from 'lodash'
import $ from 'cash-dom'
import {fmt, s, greyscale, palette, rewind, ffwd, cancel, offset, sfc32, quadMinMax, bezierMinMax} from './util.js'

export default class Arc{
  constructor({book, page, legend}){
    _.assign(this, {book, page, legend})
  }

  attach(dom){
    dom = $(dom).find('.diagram svg')
    let [width, height] = _.map(dom.attr('viewBox').split(' ').slice(2), _.toNumber)
    _.assign(this, {dom, width, height})

    return this
  }

  setDepth(depth){
    let {choices, endings, depth:maxDepth} = this.book.stats,
        pct = depth % 1,
        inv = 1 - pct,
        tendrils = []

    let {arcs, pages} = this.layout

    if (pct==0 && depth<maxDepth){
      // draw white dots for all non-ending pages at this depth
      tendrils = _.filter(pages, ({depth:d, ending}) => d==depth && !ending)
                      .map( ({x,y,w}) => `<circle fill="white" cx="${fmt(x+w/2)}"  cy="${fmt(y)}"  r="2" />`)

    }else if (pct){
      tendrils = _.filter(arcs, b => b.depth!=depth && b.depth==_.ceil(depth)).map( arc => {
        let {src, dst, ctrl, weight, loop} = arc,
            color = loop ? 'green' : palette(1-weight),
            dashes = loop ? 'stroke-dasharray="2,2"' : ''

        // from the cabinet of De Casteljau
        let q0 = {x:inv*src.x + pct*ctrl.x, y:inv*src.y + pct*ctrl.y},
            q1 = {x:inv*ctrl.x + pct*dst.x, y:inv*ctrl.y + pct*dst.y},
            r0 = {x:inv*q0.x + pct*q1.x, y:inv*q0.y + pct*q1.y}

        return `<path class="tendril" stroke="${color}" ${dashes} d="${[
          `M ${fmt(src.x)} ${fmt(src.y)}`,
          `Q ${fmt(q0.x)} ${fmt(q0.y)} ${fmt(r0.x)} ${fmt(r0.y)}`
        ].join(' ')}"/>` + `<circle fill="white" cx="${fmt(r0.x)}"  cy="${fmt(r0.y)}"  r="2" />`

      })
    }

    this.dom.find('.tendrils').html(tendrils.join(' '))


  }


  get layout(){
    let {book, page} = this,
        {paths, choices, endings, illustrations} = book,
        {counts, maxCount, depths} = book.stats.choices,
        pageDepth = pg => book.stats.pages.depths[pg]

    return {
      arcs:_.map(_.sortBy(_.toPairs(counts), ([p,c]) => c), ([pair, count]) => {
        let [src, dst] = _.split(pair, ',', 2),
            depth = depths[pair],
            loop = pageDepth(dst) < pageDepth(src),
            x0 = (src-1) * page.spacing + page.width/2,
            x = (dst-1) * page.spacing + page.width/2,
            y = (x0 < x) ? 0 : page.height,
            rng = x - x0,
            cx = x0 + rng * 1.0,
            cy = y - rng * 0.8
        return {src:{x:x0, y}, dst:{x, y}, ctrl:{x:cx, y:cy}, weight:count/maxCount, depth, loop}
      }),

      pages:_.map(_.range(book.pages), i => (
        {
          number:i+1,
          depth:book.stats.pages.depths[i+1],
          x:i*page.spacing, y:0,
          w:page.width, h:page.height,
          ending:_.includes(endings, i+1),
          illo:illustrations[i+1],
          choices:_.size(choices[i+1])
        }
      ))
    }
  }


  get svg(){
    let {book, layout, legend} = this,
        {paths, choices, endings, illustrations} = book,
        counts = _.countBy(_.flatMap(paths, pages => _.zip(pages.slice(0,-1), pages.slice(1)))),
        maxCount = _.max(_.values(counts)),
        nib = 1.5,
        style = `<style>
          .arc text{fill:#888; font:9px "Montserrat"; }
          .arc .branch{ stroke-width: 1; }
          .arc .ending{ fill: skyblue; }
          .arc .marker{ stroke:#888; stroke-width:${nib}; }
        </style>`

    let last = _.last(layout.pages),
        hi = _.maxBy(layout.arcs, 'ctrl.y'),
        lo = _.minBy(layout.arcs, 'ctrl.y'),
        boxes = [hi, lo].map( ({src, ctrl, dst}) =>
          quadMinMax(src.x, src.y, ctrl.x, ctrl.y, dst.x, dst.y)
        ),
        left = 0,
        top = _.min(_.map(boxes, 'min.y').concat(0)) - 2,
        bot = _.max(_.map(boxes, 'max.y').concat(0)) + 2,
        dim = _.max([-top, bot]),
        width = last.x + last.w,
        // height = bot - top,
        height = dim*2,
        key

    if (legend){
      left -= nib
      width += 2*nib
      key = `<g class="markers">
        <path class="marker"  d="M 0 9 l 0 12" />
        <text x="-${nib}" y="32" text-anchor="start">Pg. 1</text>
        <path class="marker" d="M ${last.x + last.w} 9 l 0 12" />
        <text x="${nib + last.x + last.w}" y="32" text-anchor="end">Pg. ${last.number}</text>
      </g>`
    }

    return `<svg class="arc" xmlns="http://www.w3.org/2000/svg" viewBox="${left} ${-dim} ${width} ${height}">${
      _.flattenDeep([
        style,
        `<g fill="none">`,
        key,

        _.map(layout.arcs, ({src, dst, ctrl, weight, depth, loop}) =>
          `<path class="branch depth-${depth}" stroke="${loop ? 'green' : palette(1 - weight)}" stroke-dasharray="${loop? "2,2" : ""}" d="M ${fmt(src.x)} ${fmt(src.y)} Q ${fmt(ctrl.x)} ${fmt(ctrl.y)} ${fmt(dst.x)} ${fmt(dst.y)}" />`
        ),

        _.map(layout.pages, pg => {
          let pageClass = pg.ending ? 'ending' : 'page'

          let clr = pg.choices > 1 ? '#737373'
                  : pg.choices ? '#2b2b2b'
                  : 'black'

          return `<rect rx=".5" class="${pageClass} depth-${pg.depth}" fill="${clr}" x="${pg.x}" y="${pg.y}" width="${pg.w}" height="${pg.h}" />`
        }),
        `<g class="tendrils"></g>`,
        '</g>',
      ]).join('')
    }</svg>`
  }
}
